import { useCallback, useEffect, useState } from 'react';
import { loadUserConfig } from '@loomstateorg/shared-frontend/hooks/useUserConfig';

// Keep in sync with PasswordPolicy in the shared backend.
export const PASSWORD_MAX_LENGTH = 35;
export const DEFAULT_PASSWORD_REGEX = `^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,${PASSWORD_MAX_LENGTH}}$`;

const isValidRegex = (pattern: unknown): pattern is string => {
  if (typeof pattern !== 'string' || !pattern) return false;
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
};

/**
 * Reads `{ regex, message }` from userConfig `passwordRules` (the backend fills `regex` from the Studio
 * `PASSWORD_REGEX` project configuration); both must be valid or both defaults apply.
 * `message` is undefined for the defaults so callers show the translated `password.not.valid`.
 */
export const usePasswordPolicy = () => {
  const [policy, setPolicy] = useState<{ regex: string; message?: string }>({ regex: DEFAULT_PASSWORD_REGEX });

  useEffect(() => {
    loadUserConfig().then((config) => {
      const { regex, message } = (config.passwordRules ?? {}) as Record<string, unknown>;
      if (isValidRegex(regex) && typeof message === 'string' && message.trim()) {
        setPolicy({ regex, message });
      }
    });
  }, []);

  return policy;
};

const useValidate = () => {
  const validateTextExists = useCallback((name: string) => name.length >= 1, []);

  const validatePassword = useCallback(
    (password: string, regex: string = DEFAULT_PASSWORD_REGEX) =>
      password.length <= PASSWORD_MAX_LENGTH && new RegExp(regex).test(password),
    [],
  );

  const validateEmail = useCallback((email: string) => {
    const emailRules =
      /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
    return emailRules.test(email);
  }, []);

  const validatePostalCode = useCallback((value: string) => Number.isInteger(Number(value)) && value.length === 5, []);

  return { validatePassword, validateEmail, validateTextExists, validatePostalCode };
};

export default useValidate;
